import { FC } from "react"
import {
  ConnectorFieldMapping,
  ConnectorDataType,
  ConnectorDirection
} from "../../../../../../types"
import { useFieldMapping } from "../hooks/useFieldMapping"
import { useValueTransforms } from "../hooks/useValueTransforms"
import { useFieldExpansion } from "../hooks/useFieldExpansion"
import FieldMappingRow from "./components/FieldMappingRow"
import AddFieldSection from "./components/AddFieldSection"

interface Props {
  fieldMapping: ConnectorFieldMapping
  dataType: ConnectorDataType
  direction: ConnectorDirection
  onChange: (mapping: ConnectorFieldMapping) => void
}

const FieldMapper: FC<Props> = ({
  fieldMapping,
  dataType,
  direction,
  onChange
}) => {
  // Custom hooks for business logic
  const {
    isIncoming,
    newBbFieldToAdd,
    setNewBbFieldToAdd,
    newCrmFieldToAdd,
    setNewCrmFieldToAdd,
    getFieldNames,
    getStableId,
    getAvailableFields,
    addFieldMapping,
    addCustomFieldMapping,
    removeFieldMapping,
    updateMapping
  } = useFieldMapping({ fieldMapping, dataType, direction, onChange })

  const { toggleExpanded, collapseField, isExpanded } = useFieldExpansion()

  const {
    getValueTransforms,
    updateValueTransform,
    removeValueTransform,
    removeAllValueTransforms
  } = useValueTransforms({
    fieldMapping,
    isIncoming,
    onChange
  })

  // Handle field removal with cleanup
  const handleRemoveField = (index: number) => {
    const stableKey = getStableId(index)
    collapseField(stableKey)
    removeFieldMapping(index)
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-6 gap-2 text-sm font-medium text-gray-700 pb-2 border-b">
        <div className="col-span-2">
          {isIncoming ? "CRM Field Name" : "Banking Bridge Field"}
        </div>
        <div className="col-span-3">
          {isIncoming ? "Banking Bridge Field" : "CRM Field Name"}
        </div>
        <div></div>
      </div>

      {fieldMapping.map((entry, index) => {
        const { bbField, crmField } = getFieldNames(entry)
        const valueTransforms = getValueTransforms(entry)
        const stableKey = getStableId(index)

        return (
          <FieldMappingRow
            key={stableKey}
            stableKey={stableKey}
            index={index}
            entry={entry}
            isIncoming={isIncoming}
            dataType={dataType}
            bbField={bbField}
            crmField={crmField}
            isExpanded={isExpanded(stableKey)}
            onUpdateMapping={updateMapping}
            onRemove={handleRemoveField}
            onToggleExpanded={() => toggleExpanded(stableKey)}
            onUpdateValueTransform={updateValueTransform}
            onRemoveValueTransform={removeValueTransform}
            onRemoveAllValueTransforms={removeAllValueTransforms}
            onCollapseField={() => collapseField(stableKey)}
            valueTransforms={valueTransforms}
          />
        )
      })}

      <AddFieldSection
        isIncoming={isIncoming}
        dataType={dataType}
        newBbFieldToAdd={newBbFieldToAdd}
        newCrmFieldToAdd={newCrmFieldToAdd}
        availableFields={getAvailableFields()}
        onBbFieldChange={setNewBbFieldToAdd}
        onCrmFieldChange={setNewCrmFieldToAdd}
        onAdd={addFieldMapping}
        onAddCustom={addCustomFieldMapping}
      />
    </div>
  )
}

export default FieldMapper
